import { EventEmitter, Injectable } from "@angular/core";
import { BotidService } from "@shared-angular/services/ragai/botid.service";
import { stageAddSchema, stageGoToIndexSchema, stageGoToNameSchema, stageGoToRandomSchema, stageListSchema } from "@shared/ragai.schemas";
import { z } from "zod";
import { DOMManagerService } from "./dommanager.service";
import { EventManagerService } from "./event-manager/event-manager.service";
import { KeyValService } from "./key-val.service";
import { SkillService } from "./skill.service";


type StageChange = {
  from?: StageDefinition;
  to?: StageDefinition;
};


type StageDefinition = {
  title: string;
  name: string;
  hidden?: boolean;
  skills: string[];

  forceSkills?: boolean;
  footer: boolean;
  enterFn?: () => Promise<void>;
  exitFn?: () => Promise<void>;
  sortFloat: number;
};
/**
 * Verwaltet Stages (Bot-Abschnitte) und die Navigation dazwischen (`RAGAI.STAGE`).
 * @remarks
 * Eine Stage ist ein benannter Zustand des Bots mit eigener Skill-Auswahl, optionalem Enter-/Exit-Callback und
 * Footer-Sichtbarkeit - das Grundgerüst für mehrstufige Dialoge, Quiz oder geführte Abläufe (State Machine).
 * Beim Wechsel läuft immer erst `exitFn` der alten, dann `enterFn` der neuen Stage. `hidden`-Stages bleiben über
 * `goToName()`/`goToIndex()` direkt ansteuerbar, tauchen aber nicht in `visibleStages` (z. B. für `RAGAI.CARDS`-Navigationslisten) auf.
 * `sortFloat` bestimmt nur die Reihenfolge für `next()`/`prev()`/`goToIndex()`, nicht die Registrierungsreihenfolge.
 * `goTo()` akzeptiert neben Index/Name auch die Spezialwerte `"___PREV_STAGE___"`, `"___NEXT_STAGE___"`,
 * `"___FIRST_STAGE___"`, `"___LAST_STAGE___"` (u. a. für generierten Blockly-Code).
 */
@Injectable({ providedIn: 'root' })
export class StageManagerService {
  private _stages: StageDefinition[] = [];
  private _currentIndex: number = -1;
  public currentStage: StageDefinition | undefined = undefined;
  private previousStage: StageDefinition | undefined = undefined;
  private KEYVAL_TYPE = "STAGE_MANAGER";
  private KEYVAL_KEY_CURRENT = "CURRENT_STAGE";
  private KEYVAL_KEY_PREVIOUS = "PREVIOUS_STAGE";
  public beforeStageChange = new EventEmitter<StageChange>();
  public afterStageChange = new EventEmitter<StageChange>();
  public stageEnter = new EventEmitter<StageDefinition>();
  public stageExit = new EventEmitter<StageDefinition>();
  private eventNamePrefix = "RAGAI.STAGES";


  constructor(private skillManager: SkillService,

    private keyValService: KeyValService,
    private DOM: DOMManagerService,
    private botIdService: BotidService,
    private eventManager: EventManagerService
  ) { }

  /**
   * Fügt eine neue Stage hinzu.
   * @param name Name der neuen Stage, oder ein Objekt gemäß `stageAddSchema`.
   * @param stage Weitere Stage-Eigenschaften (Titel, Skills, Sichtbarkeit etc.).
   */
  public add(name: string | z.input<typeof stageAddSchema>, stage?: Partial<Omit<StageDefinition, "name">>): void {
    const parsed = stageAddSchema.safeParse(name);
    if (parsed.success && typeof name === 'object') {
      const { name: n, ...rest } = parsed.data;
      name = n;
      stage = rest;
    }
    const nameStr = name as string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const fnRegistry = (window as any).__RAGAI_STAGE_FN;
    const fullStage: StageDefinition = {
      title: stage?.title ?? '',
      name: nameStr,
      hidden: stage?.hidden ?? false,
      skills: stage?.skills ?? [],
      forceSkills: stage?.forceSkills ?? false,
      footer: stage?.footer ?? true,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      enterFn: fnRegistry?.[nameStr + "_enter"] ?? undefined,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      exitFn: fnRegistry?.[nameStr + "_exit"] ?? undefined,
      sortFloat: stage?.sortFloat ?? -1
    };
    if (this._stages.find(s => s.name === nameStr)) {
      throw new Error(`Stage with name '${nameStr}' already exists.`);
    }
    this._stages.push({ ...fullStage, name: nameStr });
  }


  /**
   * Entfernt eine Stage.
   * @param name Name der zu entfernenden Stage, oder ein Objekt gemäß `stageGoToNameSchema`.
   */
  public remove(name: string | z.input<typeof stageGoToNameSchema>): void {
    const parsed = stageGoToNameSchema.safeParse(name);
    if (parsed.success && typeof name === 'object') {
      name = parsed.data.name;
    }
    const nameStr = name as string;
    this.eventManager._trigger_RAGAI_EVENT(this, { name: nameStr });
    const index = this._stages.findIndex(s => s.name === nameStr);
    if (index === -1) throw new Error(`Stage with name '${nameStr}' not found.`);
    this._stages.splice(index, 1);
    if (this._currentIndex === index) this._currentIndex = -1;
    else if (this._currentIndex > index) this._currentIndex--;
  }

  /**
   * Blendet eine Stage aus der Stage-Liste aus.
   * @param name Name der auszublendenden Stage, oder ein Objekt gemäß `stageGoToNameSchema`.
   */
  public hide(name: string | z.input<typeof stageGoToNameSchema>): void {
    const parsed = stageGoToNameSchema.safeParse(name);
    if (parsed.success && typeof name === 'object') {
      name = parsed.data.name;
    }
    const nameStr = name as string;
    const index = this._stages.findIndex(s => s.name === nameStr);
    if (index === -1) throw new Error(`Stage with name '${nameStr}' not found.`);
    if (this._stages[index]) {
      this._stages[index].hidden = true;
      if (this._currentIndex === index) this._currentIndex = -1;
      else if (this._currentIndex > index) this._currentIndex--;
    }
  }

  /**
   * Blendet eine zuvor ausgeblendete Stage wieder ein.
   * @param name Name der einzublendenden Stage, oder ein Objekt gemäß `stageGoToNameSchema`.
   */
  public show(name: string | z.input<typeof stageGoToNameSchema>): void {
    const parsed = stageGoToNameSchema.safeParse(name);
    if (parsed.success && typeof name === 'object') {
      name = parsed.data.name;
    }
    const nameStr = name as string;
    const index = this._stages.findIndex(s => s.name === nameStr);
    if (index === -1) throw new Error(`Stage with name '${nameStr}' not found.`);
    if (this._stages[index]) {
      this._stages[index].hidden = false;
      if (this._currentIndex === index) this._currentIndex = -1;
      else if (this._currentIndex > index) this._currentIndex--;
    }
  }

  /**
   * Navigiert zu einer zufälligen (sichtbaren) Stage.
   * @param possibleNames Optionale Einschränkung auf bestimmte Stage-Namen, oder ein Objekt gemäß `stageGoToRandomSchema`.
   * @returns Nichts.
   */
  public async goToRandom(possibleNames?: string[] | z.input<typeof stageGoToRandomSchema>): Promise<void> {
    const parsed = stageGoToRandomSchema.safeParse(possibleNames);
    if (parsed.success && typeof possibleNames === 'object' && !Array.isArray(possibleNames)) {
      possibleNames = parsed.data.possibleNames;
    }
    let candidates: StageDefinition[];
    if (possibleNames && (possibleNames as string[]).length > 0) {
      candidates = this._stages.filter(stage =>
        (possibleNames as string[]).includes(stage.name)
      );
    } else {
      candidates = this._stages.filter(stage => !stage.hidden);
    }

    if (candidates.length === 0) {
      throw new Error("No matching stages available.");
    }

    const randomStage = candidates[Math.floor(Math.random() * candidates.length)];
    if (randomStage) {
      await this.goToName(randomStage.name);
    }
  }


  /**
   * Navigiert zu einer Stage per Index.
   * @param index Index der Ziel-Stage, oder ein Objekt gemäß `stageGoToIndexSchema`.
   * @returns Nichts.
   */
  public async goToIndex(index: number | z.input<typeof stageGoToIndexSchema>): Promise<void> {
    const parsed = stageGoToIndexSchema.safeParse(index);
    if (parsed.success && typeof index === 'object') {
      index = parsed.data.index;
    }
    this.eventManager._trigger_RAGAI_EVENT(this, { index });
    if ((index as number) < 0 || (index as number) >= this._stages.length) {
      throw new Error(`Stage with index '${(index as number) + ""}' not found.`);
    }
    if (this._stages[index as number]) {
      this._currentIndex = index as number;
      await this._activate();
    }
  }




  /**
   * Navigiert zu einer Stage per Index, Name oder Spezial-Wert (`___PREV_STAGE___`, `___NEXT_STAGE___`, `___FIRST_STAGE___`, `___LAST_STAGE___`).
   * @param value Ziel-Index, Stage-Name oder Spezial-Wert.
   * @returns Nichts.
   */
  public async goTo(value: number | string): Promise<void> {

    if (value === "___PREV_STAGE___") {
      return this.prev();
    } else if (value === "___NEXT_STAGE___") {
      return this.next();
    } else if (value === "___FIRST_STAGE___") {
      return this.goToIndex(0);
    } else if (value === "___LAST_STAGE___") {
      return this.goToIndex(this._stages.length - 1);
    }

    this.eventManager._trigger_RAGAI_EVENT(this, { value });
    if (typeof value === 'number') {
      return this.goToIndex(value);
    } else if (typeof value === 'string') {
      return this.goToName(value);
    } else {
      throw new Error("Invalid type for goTo. Must be number or string.");
    }
  }

  /**
   * Navigiert zu einer Stage per Name.
   * @param name Name der Ziel-Stage, oder ein Objekt gemäß `stageGoToNameSchema`.
   * @returns Nichts.
   */
  public async goToName(name: string | z.input<typeof stageGoToNameSchema>): Promise<void> {
    const parsed = stageGoToNameSchema.safeParse(name);
    if (parsed.success && typeof name === 'object') {
      name = parsed.data.name;
    }
    const nameStr = name as string;
    this.eventManager._trigger_RAGAI_EVENT(this, { name: nameStr });
    const index = this._stages.findIndex(s => s.name === nameStr);
    if (index === -1) throw new Error(`Stage with name '${nameStr}' not found.`);
    if (this._stages[index]) {
      this._currentIndex = index;
      await this._activate();
    }
  }


  /**
   * Navigiert zur nächsten Stage.
   * @returns Nichts.
   */
  public async next() {
    this.eventManager._trigger_RAGAI_EVENT(this);
    if (this._currentIndex < this._stages.length - 1) {
      this._currentIndex++;
      await this._activate();
    } else {
      throw new Error('Already at the last stage.');
    }
  }

  /**
   * Navigiert zur vorherigen Stage.
   * @returns Nichts.
   */
  public async prev() {
    this.eventManager._trigger_RAGAI_EVENT(this);
    if (this._currentIndex > 0) {
      this._currentIndex--;
      await this._activate();
    } else {
      throw new Error('Already at the first stage.');
    }
  }

  /* public get currentStage(): StageDefinition | undefined {
    return this._stages[this._currentIndex];
  } */

  public get allStages(): StageDefinition[] {
    return [...this._stages];
  }

  public get visibleStages(): StageDefinition[] {
    return this._stages.filter(stage => !stage.hidden);
  }

  /**
   * Stellt die zuletzt gespeicherte Stage wieder her (z. B. nach Seiten-Reload).
   * @returns Nichts.
   */
  public async restore(): Promise<void> {
    const savedStageName = await this.keyValService.get(this.KEYVAL_KEY_CURRENT, this.KEYVAL_TYPE);




    if (savedStageName && typeof savedStageName === "string") {
      const index = this._stages.findIndex(stage => stage.name === savedStageName);
      if (index !== -1) {
        this._currentIndex = index;
        // Damit EnterJS der neuen Stage auch ausgeführt wird
        // this.previousStage = this._stages[this._currentIndex];
        // this.currentStage = this._stages[this._currentIndex];
        await this._activate();

      } else {
        console.warn(`⚠️ Stage '${savedStageName}' not found in current stage list.`);
      }
    } else {
      console.log("ℹ️ No saved stage to restore.");
    }
  }


  /**
   * Gibt die Namen aller Stages zurück.
   * @param includeHidden `true`, um auch ausgeblendete Stages einzuschließen, oder ein Objekt gemäß `stageListSchema`.
   * @returns Liste der Stage-Namen.
   */
  public list(includeHidden: boolean | z.input<typeof stageListSchema> = false): string[] {
    const parsed = stageListSchema.safeParse(includeHidden);
    if (parsed.success && typeof includeHidden === 'object') {
      includeHidden = parsed.data.includeHidden ?? false;
    }
    return this._stages
      .filter(stage => (includeHidden as boolean) || !stage.hidden)
      .map(stage => stage.name);
  }



  private async _activate() {
    const from = this.currentStage;
    const to = this._stages[this._currentIndex] ?? undefined;
    const isSameStage = from?.name + "" === to?.name + "";
    console.log("🎭 Aktiviere Stage", from, to);
    // 👉 Before-Event
    if (!isSameStage) {
      this.beforeStageChange.emit({ from, to });
    }
    // 👉 Exit-Event
    if (from && !isSameStage) {
      this.stageExit.emit(from);
    }
    this.previousStage = from;

    // WENN ALT STAGE == NEU STAGE, DANN KEINE SKILLS NICHT ANFASSEN
    if (!isSameStage) {
      await this.skillManager.disableAllSkills();
      if (to && to.skills.length > 0 && this.botIdService.getBotId()) {
        await this.skillManager.enableSkills(to.skills);
      }

      if (to?.forceSkills && this.botIdService.getBotId()) {
        await this.skillManager.skillsForNextRun(to.skills);
      } else if (this.botIdService.getBotId()) {
        await this.skillManager.skillsForNextRun([]);
      }
    }


    if (to?.footer) {
      this.DOM.showFooter();
    } else {
      this.DOM.hideFooter();
    }

    document.title = to?.title ?? "";
    await this.keyValService.storeKeyValEntry(this.KEYVAL_KEY_CURRENT, to?.name, this.KEYVAL_TYPE);
    await this.keyValService.storeKeyValEntry(this.KEYVAL_KEY_PREVIOUS, from?.name, this.KEYVAL_TYPE);

    this.currentStage = to;

    // 👉 Enter-Event
    if (!isSameStage) {
      this.stageEnter.emit(to);
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.STAGE.ENTER", to);
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.STAGE.EXIT", from);
      // Exit alte Stage, dann Enter neue Stage
      try {
        if (from?.exitFn) await from.exitFn();
      } catch (e) {
        console.error("Stage exitJS error:", e);
      }
      try {
        if (to?.enterFn) await to.enterFn();
      } catch (e) {
        console.error("Stage enterJS error:", e);
      }
    }

    // 👉 After-Event
    this.afterStageChange.emit({ from, to });
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.STAGE.CHANGED", { from, to });
  }

}
